import { useMemo, useRef } from 'react';
import type { MouseEventHandler } from 'react';
import { BusinessLoggerDefinition } from '@wix/site-service-business-logger/definition';
import type { IBusinessLoggerService } from '@wix/site-service-business-logger/definition';
import { ConfigurationDefinition } from '@wix/site-service-configuration/definition';
import type { IConfigurationService } from '@wix/site-service-configuration/definition';
import { EnvironmentDefinition } from '@wix/viewer-service-environment/definition';
import type { IEnvironmentService } from '@wix/viewer-service-environment/definition';
import { useService } from '@wix/services-manager-react';
import {
  buildClickAnalyticsEvent,
  CLICK_ANALYTICS_ENDPOINT,
} from '../clickAnalytics';
import type { ClickAnalyticsParams } from '../clickAnalytics';

export const useClickAnalyticsReporter = (
  params: ClickAnalyticsParams,
): MouseEventHandler | undefined => {
  const businessLogger = useService(
    BusinessLoggerDefinition,
  ) as IBusinessLoggerService;
  const environment = useService(EnvironmentDefinition) as IEnvironmentService;
  const configuration = useService(
    ConfigurationDefinition,
  ) as IConfigurationService;

  const isPreview = environment?.getPreviewMode?.();
  const reportBi = businessLogger?.reportBi;
  const language = params.language ?? environment?.language;
  const trackClicksAnalytics = configuration?.trackClicksAnalytics;
  const isEnabled =
    !isPreview && trackClicksAnalytics && typeof reportBi === 'function';

  const paramsRef = useRef(params);
  paramsRef.current = params;

  return useMemo(() => {
    if (!isEnabled) {
      return undefined;
    }

    return event => {
      const { elementId, ...restParams } = paramsRef.current;

      try {
        void Promise.resolve(
          reportBi(
            buildClickAnalyticsEvent({
              ...restParams,
              language,
              elementId: elementId || event.currentTarget.id,
            }),
            { endpoint: CLICK_ANALYTICS_ENDPOINT },
          ),
        ).catch(() => {});
      } catch {
        // reporting must never break the component interaction
      }
    };
  }, [isEnabled, reportBi, language]);
};
